// FIX-CCS-OUTBOX (spec §7.1, §7.2, §7.4) — writing a photo into the outbox at Save, plus the two
// browser-storage helpers (persistent storage at first login; the iPhone Safari-tab tip).
//
// Save writes the item FIRST (before any network), so "Saved" can show within 2 s. Context comes from
// the phone's own caches (never a network wait); what is missing is filled by the server at send and the
// item says context_as_of = 'send'. The install id and sequence number are frozen into the item here.

import { getClientInstallId, nextClientSeq, type CaptureClaim } from '@/features/capture/lib/captureClaims';
import type { PreparedUpload } from '@/features/capture/lib/originalUpload';
import { APP_BUILD } from '@/shared/lib/appBuild';
import { checkCanQueue, sha256Hex, type QueueRefusal, type SpaceEstimate } from './limits';
import type { OutboxStore } from './outboxStore';
import { OUTBOX_ITEM_SCHEMA, type OutboxContext, type OutboxGpsFix, type OutboxItem } from './types';

export interface GpsAtPick {
  latitude: number;
  longitude: number;
  accuracy: number | null;
  /** Geolocation position.timestamp (ms). */
  timestamp: number;
}

export interface EnqueueInput {
  store: OutboxStore;
  userId: string;
  photoId: string;
  prepared: PreparedUpload;
  claim: CaptureClaim;
  category: string;
  context: OutboxContext;
  gps: GpsAtPick | null;
  /** FIX-CCS-G-GPS: the Location Bundle when known at save ('final'), or 'holding' until the engine resolves it. */
  location?: { state: 'holding' | 'final'; bundle: Record<string, unknown> | null };
  estimate: SpaceEstimate | null;
  nowMs?: number;
}

export type EnqueueResult = { ok: true; item: OutboxItem } | { ok: false; refused: QueueRefusal };

const EXT: Record<'jpeg' | 'png' | 'webp', 'jpg' | 'png' | 'webp'> = { jpeg: 'jpg', png: 'png', webp: 'webp' };

export function gpsFixAtPick(gps: GpsAtPick | null, pickedAtIso: string): OutboxGpsFix | null {
  if (!gps || !Number.isFinite(gps.latitude) || !Number.isFinite(gps.longitude)) return null;
  const pickedMs = Date.parse(pickedAtIso);
  const fixMs = Number.isFinite(gps.timestamp) && gps.timestamp > 0 ? gps.timestamp : pickedMs;
  return {
    lat: gps.latitude,
    lng: gps.longitude,
    accuracy_m: typeof gps.accuracy === 'number' && Number.isFinite(gps.accuracy) ? gps.accuracy : null,
    fix_at: new Date(fixMs).toISOString(),
    age_s_at_pick: Math.max(0, Math.round((pickedMs - fixMs) / 1000)),
  };
}

/** SD-O50 (R18): the cheap, synchronous part of the pre-capture record — build, device, page — stamped at enqueue, so
 *  a queued photo carries it even when the slow enrichment never finishes offline. Same shapes as the live path's
 *  gatherPreCaptureMetadata (src/utils/photoPreCapture.ts); attachPrecapture merges the rest on top when it arrives. */
export function enqueueStamp(): Record<string, unknown> {
  const nav = typeof navigator === 'undefined' ? null : navigator;
  const doc = typeof document === 'undefined' ? null : document;
  let timezone: string | null = null;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    /* unsupported */
  }
  return {
    schema_version: 3,
    app: { ...APP_BUILD },
    device: { user_agent: nav?.userAgent ?? null, platform: nav?.platform ?? null, language: nav?.language ?? null, timezone },
    page: { url: typeof window === 'undefined' ? null : window.location?.href ?? null, referrer: doc?.referrer ?? null, visibility: doc?.visibilityState ?? null },
  };
}

/** The slow enrichment lands on top of the enqueue stamp; a non-object on either side is ignored. */
export function mergePrecapture(base: unknown, extra: unknown): Record<string, unknown> {
  const obj = (v: unknown) => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
  return { ...obj(base), ...obj(extra) };
}

export async function enqueuePhoto(input: EnqueueInput): Promise<EnqueueResult> {
  const nowMs = input.nowMs ?? Date.now();
  const { prepared } = input;
  const sha = await sha256Hex(prepared.original);
  const existing = await input.store.all();
  const refusal = checkCanQueue(existing, prepared.original.size, sha, input.estimate);
  if (refusal) return { ok: false, refused: refusal };

  const item: OutboxItem = {
    schema: OUTBOX_ITEM_SCHEMA,
    photo_id: input.photoId,
    owner_user_id: input.userId,
    state: 'pending',
    original: prepared.original,
    original_ext: EXT[prepared.detected],
    preview: prepared.thumbnail,
    upload_transform: prepared.transform,
    size_bytes: prepared.original.size,
    sha256: sha,
    category: input.category,
    context: input.context,
    context_as_of: input.context.cycle_id ? 'capture' : 'send',
    gps: gpsFixAtPick(input.gps, input.claim.picked_at_client),
    ...(input.location ? { location_bundle: input.location.bundle, location_state: input.location.state } : {}),
    claims: {
      picked_at_client: input.claim.picked_at_client,
      captured_at_client: input.claim.captured_at_client,
      offline_at_pick: input.claim.offline_at_pick,
      client_offset_before_s: input.claim.client_offset_before_s,
      client_install_id: getClientInstallId(),
      client_seq: nextClientSeq(),
    },
    precapture: enqueueStamp(), // SD-O50: app/device/page from the start; the enrichment is merged in later
    queued_at: new Date(nowMs).toISOString(),
    save_attempt_done: false,
    send_failed_before: false,
    attempts: 0,
    last_error: null,
    last_error_detail: null,
    last_attempt_at: null,
    next_attempt_at: null,
  };
  await input.store.put(item);
  return { ok: true, item };
}

/** navigator.storage.estimate(), or null where unsupported / failing. */
export async function readSpaceEstimate(): Promise<SpaceEstimate | null> {
  try {
    if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return null;
    const e = await navigator.storage.estimate();
    return { usage: e.usage, quota: e.quota };
  } catch {
    return null;
  }
}

export interface PersistRecord {
  asked_at: string;
  granted: boolean;
  supported: boolean;
}

/** Spec §7.1: on the first successful login, ask the browser to keep this origin's storage; record the answer once. */
export async function requestPersistentStorageOnce(store: OutboxStore): Promise<PersistRecord | null> {
  try {
    const prior = await store.getMeta<PersistRecord>('persist_record');
    if (prior) return prior;
    const supported = typeof navigator !== 'undefined' && !!navigator.storage?.persist;
    let granted = false;
    if (supported) {
      granted = (await navigator.storage.persisted?.()) === true || (await navigator.storage.persist()) === true;
    }
    const rec: PersistRecord = { asked_at: new Date().toISOString(), granted, supported };
    await store.setMeta('persist_record', rec);
    console.info('[outbox] persistent storage', rec);
    return rec;
  } catch (e) {
    console.warn('[outbox] persistent storage request failed', e);
    return null;
  }
}

/** iPhone/iPad Safari in a browser tab (not added to the Home Screen): storage may be cleared after 7 days without use. */
export function isIosBrowserTab(): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1);
  if (!ios) return false;
  const standalone =
    (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return !standalone;
}
